import { sql } from "drizzle-orm";
import type { DocumentVisibility } from "@gencow/core";

export type RagCorpusPolicyDbLike = {
  execute: (query: ReturnType<typeof sql>) => Promise<unknown>;
};

export type RagCorpusPolicyRow = {
  id: string;
  corpus: string;
  visibility_scope: DocumentVisibility;
  owner_user_id: string | null;
  title: string;
  description: string | null;
  allow_external_providers: boolean;
  retention_days: number | null;
  metadata: unknown;
  created_at: string | Date;
  updated_at: string | Date;
};

function rowsFromResult<T>(result: unknown): T[] {
  if (Array.isArray(result)) return result as T[];
  if (result && typeof result === "object" && Array.isArray((result as { rows?: unknown[] }).rows)) {
    return (result as { rows: T[] }).rows;
  }
  return [];
}

function buildOwnerScopeClause(ownerUserId: string | null) {
  return ownerUserId == null ? sql`AND owner_user_id IS NULL` : sql`AND owner_user_id = ${ownerUserId}`;
}

export function parsePrivateExternalCorpusAllowlist(value: string | undefined): string[] {
  return (value ?? "")
    .split(",")
    .map((entry) => entry.trim())
    .filter(Boolean);
}

export function matchesAccessibleCorpusOwner(viewerUserId: string, ownerUserId: string | null): boolean {
  return ownerUserId == null || ownerUserId === viewerUserId;
}

export async function loadRagCorpusPolicyByScope(
  db: RagCorpusPolicyDbLike,
  params: {
    corpus: string;
    visibility: DocumentVisibility;
    ownerUserId: string | null;
  },
): Promise<RagCorpusPolicyRow | null> {
  const result = await db.execute(sql`
        SELECT *
        FROM rag_corpora
        WHERE corpus = ${params.corpus}
          AND visibility_scope = ${params.visibility}
          ${buildOwnerScopeClause(params.ownerUserId)}
        LIMIT 1
    `);
  return rowsFromResult<RagCorpusPolicyRow>(result)[0] ?? null;
}

export async function loadAccessibleRagCorpusPolicy(
  db: RagCorpusPolicyDbLike,
  params: {
    corpus: string;
    visibility: DocumentVisibility;
    ownerUserId: string | null;
    viewerUserId: string;
  },
): Promise<RagCorpusPolicyRow | null> {
  const row = await loadRagCorpusPolicyByScope(db, params);
  if (!row || !matchesAccessibleCorpusOwner(params.viewerUserId, row.owner_user_id)) {
    return null;
  }
  return row;
}

export async function resolveAllowExternalProvidersPolicy(params: {
  db?: RagCorpusPolicyDbLike;
  corpus: string;
  visibility: DocumentVisibility;
  ownerUserId: string | null;
  allowPrivateExternalCorpora?: string[];
}): Promise<boolean> {
  if (params.db) {
    const row = await loadRagCorpusPolicyByScope(params.db, {
      corpus: params.corpus,
      visibility: params.visibility,
      ownerUserId: params.ownerUserId,
    });
    if (row) {
      return !!row.allow_external_providers;
    }
  }

  if (params.visibility !== "private") {
    return true;
  }

  return (params.allowPrivateExternalCorpora ?? []).includes(params.corpus);
}
